import React, { useState, useRef, useEffect } from 'react';

import {
    Button, Dialog, Grid, Icon, Typography, FormHelperText
} from '@material-ui/core';

import Scrollbars from "rc-scrollbars";
import CloseIcon from '@material-ui/icons/Close';

import DialogLoader from '../../../../components/Loader/DialogLoader';
import { InputBaseField } from '../../../../components/InputField/InputField';
import { Label, DraggableComponent } from '../../../../components/UiElements/UiElements';
import { PostDataAPI } from '../../../../Services/APIService';
import { withSnackbar } from "../../../../components/Message/Alert";

import useStyles from "./styles";
import { handleNumberKeyPress } from '../../../../components/Common/Extensions';

import cloudAdd from '../../../../assets/img/icons/cloudAdd.png';

function SendQRDialog({
    dialogOpenClose,
    handleClose,
    handleSuccessClose,
    record,
    beneficiary,
    ...props
}) {

    const classes = useStyles();
    const { showMessage } = props;

    const [isLoading, setIsLoading] = useState(false);

    const [state, setState] = useState({
        applicationId: '',
        beneficiaryId: '',
        icc: '',
        assignedPhoneNumber: '',
        activationDate: ''
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [qrImage, setQrImage] = useState({
        file: null,
        preview: null,
        fileName: "Upload QR Code"
    });

    const inputFile = useRef(null);

    /* ================= INIT STATE (FIX NULL ISSUE) ================= */

    useEffect(() => {
        if (record || beneficiary) {
            setState({
                applicationId: record?.applicationId || '',
                beneficiaryId: beneficiary?.beneficiaryId || '',
                icc: beneficiary?.icc || '',
                assignedPhoneNumber: beneficiary?.assignedPhoneNumber || '',
                activationDate: beneficiary?.activationDate || ''
            });
        }
    }, [record, beneficiary]);

    /* ================= INPUT ================= */

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');

        if (value.length > 10) value = value.slice(0, 10);

        const match = value.match(/^(\d{3})(\d{3})(\d{4})$/);

        if (match) {
            value = `(${match[1]}) ${match[2]}-${match[3]}`;
        }

        setState(prev => ({
            ...prev,
            assignedPhoneNumber: value
        }));
    };

    /* ================= FILE UPLOAD ================= */

    const uploadFile = () => inputFile.current.click();

    const uploadSingleFile = (e) => {
        if (!e.target.files?.length) return;

        const file = e.target.files[0];
        const ext = file.name.split('.').pop().toLowerCase();

        const allowed = ["png", "jpg", "jpeg", "gif"];

        if (!allowed.includes(ext)) {
            showMessage("Error", "Only PNG/JPG/JPEG/GIF allowed", "error", 3000);
            return;
        }

        if (file.size > 3 * 1024 * 1024) {
            showMessage("Error", "Max file size is 3MB", "error", 3000);
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setQrImage({
                file,
                preview: reader.result,
                fileName: file.name
            });
        };

        reader.readAsDataURL(file);
    };

    /* ================= VALIDATION ================= */

    const validatePhone = (val) => {
        const re = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
        return re.test(val);
    };
    const getSafeValue = (val) => {
        return (val === null || val === "null" || val === "undefined" || val === undefined)
            ? ''
            : val;
    };
    const Validate = () => {
        let errors = {};

        if (!state.icc || state.icc.length < 19) {
            errors.errorIcc = true;
        }

        if (!state.assignedPhoneNumber) {
            errors.errorPhone = true;
        } else if (!validatePhone(state.assignedPhoneNumber)) {
            errors.errorPhoneLength = true;
        }

        if (!state.activationDate) {
            errors.errorActivationDate = true;
        }

        if (!qrImage.file) {
            errors.errorQR = true;
        }

        setErrorMessages(errors);

        return Object.keys(errors).length === 0;
    };

    /* ================= SUBMIT ================= */

    const handleSubmit = () => {

        if (!Validate()) return;

        const formData = new FormData();
        formData.append("File", qrImage.file);
        formData.append("ApplicationId", state.applicationId);
        formData.append("BeneficiaryId", state.beneficiaryId);
        formData.append("ICC", state.icc);
        formData.append("AssignedPhoneNumber", state.assignedPhoneNumber);
        formData.append("ActivationDate", state.activationDate);

        setIsLoading(true);

        PostDataAPI("fdnmcchromicconnect/sendQRCode", formData, true).then((result) => {
            setIsLoading(false);
            if (result.success) {
                showMessage("Success", "QR Code Sent Successfully", "success", 3000);
                handleSuccessClose(true);
            }
            else {
                showMessage("Error", "No record exists to send review request", "error", 3000);
            }

        })
    };

    /* ================= UI ================= */

    return (
        <Dialog
            classes={{ paper: classes.dialogContent }}
            open={dialogOpenClose}
            PaperComponent={DraggableComponent}
            maxWidth="lg"
        >
            <div className={classes.box}>

                {/* HEADER */}
                <div className={classes.header}>
                    <Typography className={classes.title}>
                        Send QR Code
                    </Typography>
                    <Icon className={classes.closeIcon} onClick={handleClose}>
                        <CloseIcon />
                    </Icon>
                </div>

                {isLoading && <DialogLoader />}

                <Scrollbars autoHeight autoHeightMax={500}>
                    <div className={classes.content}>

                        <Grid container spacing={2}>

                            {/* ICC */}
                            <Grid item xs={12} md={6}>
                                <Label title="SIM Card #" mandatory />
                                <InputBaseField
                                    name="icc"
                                    value={getSafeValue(state.icc)}
                                    onChange={handleChange}
                                    onKeyPress={handleNumberKeyPress}
                                />
                                {errorMessages.errorIcc &&
                                    <FormHelperText style={{ color: 'red' }}>
                                        Enter valid SIM number
                                    </FormHelperText>
                                }
                            </Grid>

                            {/* PHONE */}
                            <Grid item xs={12} md={6}>
                                <Label title="Assigned Phone #" mandatory />
                                <InputBaseField
                                    name="assignedPhoneNumber"
                                    value={getSafeValue(state.assignedPhoneNumber)}
                                    onChange={handlePhoneChange}
                                />
                                {errorMessages.errorPhone &&
                                    <FormHelperText style={{ color: 'red' }}>
                                        Enter phone number
                                    </FormHelperText>
                                }
                                {errorMessages.errorPhoneLength &&
                                    <FormHelperText style={{ color: 'red' }}>
                                        Invalid phone format
                                    </FormHelperText>
                                }
                            </Grid>

                            {/* ACTIVATION DATE */}
                            <Grid item xs={12} md={6}>
                                <Label title="Activation Date" mandatory />
                                <InputBaseField
                                    name="activationDate"
                                    value={state.activationDate}
                                    onChange={handleChange}
                                    placeholder="Activation Date"
                                    type="date"
                                    MaxLength={14}
                                />
                                {errorMessages.errorActivationDate &&
                                    <FormHelperText style={{ color: 'red' }}>
                                        Select activation date
                                    </FormHelperText>
                                }
                            </Grid>

                            {/* QR UPLOAD */}
                            <Grid item xs={12}>
                                <Label title="Upload QR Code" mandatory />

                                <div className={classes.imageUploadContainer} onClick={uploadFile}>
                                    {qrImage.preview ? (
                                        <img
                                            src={qrImage.preview}
                                            alt="QR Preview"
                                            style={{ width: 250, borderRadius: 8 }}
                                        />
                                    ) : (
                                        <>
                                            <img src={cloudAdd} alt="upload" />
                                            <p>PNG, JPG up to 3MB</p>
                                        </>
                                    )}
                                </div>

                                {errorMessages.errorQR &&
                                    <FormHelperText style={{ color: 'red' }}>
                                        Upload QR code
                                    </FormHelperText>
                                }

                                <input
                                    type="file"
                                    ref={inputFile}
                                    hidden
                                    accept=".png,.jpg,.jpeg"
                                    onChange={uploadSingleFile}
                                />
                            </Grid>

                        </Grid>
                    </div>
                </Scrollbars>

                {/* FOOTER */}
                <div className={classes.footer}>
                    <div className={classes.footerRight}>
                        <Button className={classes.backBtn} onClick={handleClose}>
                            Close
                        </Button>
                        <Button className={classes.changeBtn} onClick={handleSubmit}>
                            Send
                        </Button>
                    </div>
                </div>

            </div>
        </Dialog>
    );
}

export default withSnackbar(SendQRDialog);